import React, { useState, useEffect } from 'react';
import { ShoppingCart, Users, Globe, Shield, BarChart3, Smartphone, CheckCircle, ArrowRight, Cloud, Zap, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
const InforRhythmLanding = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: <ShoppingCart className="w-8 h-8" />,
      title: "Self-Service E-Commerce",
      description: "Empower customers with complete self-service capabilities - view catalogs, check pricing, place orders, and track shipments without support calls.",
      color: "#2563eb"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Deep ERP Integration",
      description: "Native integration with Infor ERP systems ensures real-time pricing, inventory, and customer data accuracy across all touchpoints.",
      color: "#16a34a"
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Omnichannel Experience",
      description: "Responsive design delivers consistent experiences across desktop, tablet, and mobile devices for today's digital-first customers.",
      color: "#dc2626"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Customer Personalization",
      description: "Deliver tailored catalogs, pricing, and content with role-based access and customer-specific experiences.",
      color: "#ea580c"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Real-Time Analytics",
      description: "Gain insights into customer behavior, sales trends, and performance with integrated dashboards and reporting tools.",
      color: "#374151"
    },
    {
      icon: <Cloud className="w-8 h-8" />,
      title: "Scalable Cloud Architecture",
      description: "Built on AWS infrastructure with high availability, security, and global scalability for expanding businesses.",
      color: "#1f2937"
    }
  ];

  const benefits = [
    "Improved customer satisfaction through fast, transparent service",
    "Increased sales conversions with intuitive purchasing platform",
    "Reduced internal workload through automated customer service",
    "Enhanced brand loyalty with consistent digital experiences",
    "Faster time-to-market for new products and pricing updates",
    "Global readiness with multi-language and multi-currency support"
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#ffffff' }}>
      {/* Hero Section */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#111827' }}>
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center px-4 py-2 rounded-full" style={{ backgroundColor: '#1f2937', borderColor: '#4b5563', border: '1px solid' }}>
                <Target className="w-5 h-5 mr-2" style={{ color: '#60a5fa' }} />
                <span className="font-medium" style={{ color: '#d1d5db' }}>Modern Digital Commerce Platform</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold leading-tight" style={{ color: '#ffffff' }}>
                Infor Rhythm
                <span className="block" style={{ color: '#60a5fa' }}>
                  for Commerce
                </span>
              </h1>
              
              <p className="text-xl leading-relaxed" style={{ color: '#d1d5db' }}>
                A cloud-based digital engagement platform designed for seamless B2B and B2C experiences, 
                deeply integrated with Infor ERP systems.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mt-12">
                <Link to="/contact">
                 <button className="px-8 py-4 font-semibold rounded-lg transform hover:scale-105 transition-all duration-300" style={{ backgroundColor: '#2563eb', color: '#ffffff' }}>
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </button>
                
                </Link>
               
                
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=450&fit=crop&crop=center"
                alt="Infor Rhythm Dashboard"
                className="rounded-lg w-full"
                style={{ border: '1px solid #4b5563' }}
              />
              <div className="absolute inset-0 rounded-lg" style={{ backgroundColor: 'rgba(37, 99, 235, 0.1)' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 relative" style={{ backgroundColor: '#ffffff' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#111827' }}>
              Powerful Features for Modern Commerce
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: '#4b5563' }}>
              Everything you need to create exceptional digital experiences for your customers, 
              dealers, and partners.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Feature Cards */}
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`p-6 rounded-2xl transition-all duration-300 cursor-pointer ${
                    activeFeature === index ? '' : ''
                  }`}
                  style={{
                    backgroundColor: activeFeature === index ? '#f9fafb' : '#ffffff',
                    border: activeFeature === index ? '1px solid #d1d5db' : '1px solid #e5e7eb',
                    boxShadow: activeFeature === index ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                  }}
                  onClick={() => setActiveFeature(index)}
                >
                  <div className="flex items-start space-x-4">
                    <div className="p-3 rounded-xl" style={{ backgroundColor: feature.color, color: '#ffffff' }}>
                      {feature.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2" style={{ color: '#111827' }}>
                        {feature.title}
                      </h3>
                      <p className="leading-relaxed" style={{ color: '#4b5563' }}>
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Feature Visualization */}
            <div className="relative">
              <div className="rounded-3xl p-8" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
                <div className="space-y-6">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: features[activeFeature].color, color: '#ffffff' }}>
                    {features[activeFeature].icon}
                  </div>
                  
                  <h3 className="text-2xl font-bold" style={{ color: '#111827' }}>
                    {features[activeFeature].title}
                  </h3>
                  
                  <p className="text-lg leading-relaxed" style={{ color: '#4b5563' }}>
                    {features[activeFeature].description}
                  </p>

                  {/* Feature Image */}
                  <div className="mt-8">
                    <img 
                      src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop&crop=center"
                      alt={features[activeFeature].title}
                      className="w-full rounded-xl"
                      style={{ border: '1px solid #d1d5db' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20" style={{ backgroundColor: '#f9fafb' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#111827' }}>
              Business Benefits
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: '#4b5563' }}>
              Transform your digital commerce strategy and deliver exceptional value to your organization.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group p-6 rounded-2xl transition-all duration-300"
                style={{ 
                  backgroundColor: '#ffffff', 
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}
              >
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: '#16a34a' }} />
                  <p className="leading-relaxed group-hover:transition-colors duration-300" style={{ color: '#4b5563' }}>
                    {benefit}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Integration Section */}
      <div className="py-20" style={{ backgroundColor: '#ffffff' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-3xl p-12" style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6" style={{ color: '#111827' }}>
                  Seamless ERP Integration
                </h2>
                <p className="text-xl mb-8 leading-relaxed" style={{ color: '#4b5563' }}>
                  Unlike standalone e-commerce solutions, Infor Rhythm is natively integrated with 
                  Infor ERP systems including CloudSuite Industrial, CloudSuite Distribution, and CloudSuite M3.
                </p>
                <div className="space-y-4">
                  {[
                    'Real-time pricing and inventory data',
                    'Account-specific catalogs and negotiated prices',
                    'Seamless order processing and fulfillment',
                    'Integrated customer and product information'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#2563eb' }}></div>
                      <span style={{ color: '#4b5563' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <div className="rounded-2xl p-8" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { name: 'CloudSuite Industrial', icon: <ShoppingCart className="w-5 h-5" /> },
                      { name: 'CloudSuite Distribution', icon: <Globe className="w-5 h-5" /> },
                      { name: 'CloudSuite M3', icon: <BarChart3 className="w-5 h-5" /> },
                      { name: 'Infor CPQ', icon: <Shield className="w-5 h-5" /> }
                    ].map((system, index) => (
                      <div
                        key={index}
                        className="p-4 rounded-xl text-center transition-shadow duration-300"
                        style={{ 
                          backgroundColor: '#ffffff', 
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                        }}
                      >
                        <div className="w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: '#f3f4f6', color: '#4b5563' }}>
                          {system.icon}
                        </div>
                        <span className="text-sm font-medium" style={{ color: '#374151' }}>
                          {system.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Integration Image */}
                <div className="mt-6">
                  <img 
                    src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=500&h=300&fit=crop&crop=center"
                    alt="ERP Integration"
                    className="w-full rounded-xl"
                    style={{ border: '1px solid #e5e7eb' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20" style={{ backgroundColor: '#f3f4f6' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: '#111827' }}>
            Ready to Transform Your Digital Commerce?
          </h2>
          <p className="text-xl mb-12 max-w-2xl mx-auto" style={{ color: '#4b5563' }}>
            Join forward-thinking companies who are delivering exceptional customer experiences 
            with Infor Rhythm for Commerce.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button className="px-10 py-4 font-semibold rounded-lg transform hover:scale-105 transition-all duration-300 text-lg" style={{ backgroundColor: '#2563eb', color: '#ffffff', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
              Start Your Journey
              <ArrowRight className="w-5 h-5 ml-2 inline" />
            </button>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default InforRhythmLanding;
